import type { UserProfile } from "@/pages/users/types/user";

const DEFAULT_PAY_BASE_URL = "https://pay.rockethealth.app";

function getPayBaseUrl(): string {
  const raw = (import.meta.env.VITE_ROCKETHEALTH_PAY_BASE_URL as string | undefined)?.trim();
  const base = raw && raw.length > 0 ? raw : DEFAULT_PAY_BASE_URL;
  return base.endsWith("/") ? base.slice(0, -1) : base;
}

function resolveFirstLastName(user: UserProfile): { first: string; last: string } {
  const first = user.first_name?.trim();
  const last = user.last_name?.trim();
  if (first || last) {
    return { first: first ?? "", last: last ?? "" };
  }
  const full = user.full_name?.trim() ?? "";
  const space = full.indexOf(" ");
  if (space === -1) {
    return { first: full, last: "" };
  }
  return {
    first: full.slice(0, space).trim(),
    last: full.slice(space + 1).trim(),
  };
}

function resolveEmail(user: UserProfile): string {
  return user.preferred_email?.trim() || user.emails[0]?.trim() || "";
}

/** E.164-style number with leading + for Calendly-style query params */
function resolveTextReminderNumber(user: UserProfile): string {
  const raw =
    user.preferred_phone_number?.trim() ||
    user.otpless_identifier?.trim() ||
    user.phone_numbers[0]?.trim() ||
    "";
  if (!raw) return "";
  const compact = raw.replace(/[\s-]/g, "");
  if (compact.startsWith("+")) return compact;
  if (/^\d+$/.test(compact)) {
    return `+${compact}`;
  }
  return compact;
}

/** Product lines that have a standalone reschedule-fee payment page. */
export type RescheduleFeeLine = "therapy" | "clinical" | "psychiatry";

/** Pay-app route slug per product line (see apps/payment `_helper/config.ts`). */
const RESCHEDULE_FEE_SLUG: Record<RescheduleFeeLine, string> = {
  therapy: "therapy-reschedule-fee",
  clinical: "clinical-reschedule-fee-1",
  psychiatry: "psychiatry-reschedule-fee-1",
};

/**
 * Build a reschedule-fee payment link for the given product line. The pay app
 * prefills from the query string when no Calendly `schedule` param is present
 * (see RightSection URLSearchParams branch).
 */
export function buildReschedulePaymentLink(
  user: UserProfile,
  line: RescheduleFeeLine,
): string {
  const { first, last } = resolveFirstLastName(user);
  const url = new URL(`/${RESCHEDULE_FEE_SLUG[line]}`, `${getPayBaseUrl()}/`);
  url.searchParams.set("invitee_first_name", first);
  url.searchParams.set("invitee_last_name", last);
  url.searchParams.set("invitee_email", resolveEmail(user));
  const phone = resolveTextReminderNumber(user);
  if (phone) {
    url.searchParams.set("text_reminder_number", phone);
  }
  return url.toString();
}

/** Back-compat convenience wrapper for the therapy line. */
export function buildTherapyReschedulePaymentLink(user: UserProfile): string {
  return buildReschedulePaymentLink(user, "therapy");
}
